import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { toast } from '@/lib/notifications';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { removeLifetimeFreeCallable } from '@/lib/callables';

type Props = {
  open: boolean;
  tenantId: string;
  tenantName: string;
  currentCategory: string | null;
  onClose: () => void;
};

/**
 * Encerra a cortesia de um tenant (`removeLifetimeFree`): COURTESY → DIRECT
 * **sem assinatura** — o tenant cai no paywall e o consultor gera um link novo
 * pela Contratação do CRM.
 *
 * Origem: sem issue — commit 5af121d4c, 2026-05-25 23:10 +0200 (Sprint 35, PR
 * G4). Desde a F7 da #679 (#738) a cortesia é tipo comercial, sem cupom.
 *
 * Contrato: `reason` obrigatório; `AdminConfirm` antes do envio; sucesso →
 * toast e fecha; erro → fecha o `AdminConfirm` e mostra `err.message` num
 * `Alert` dentro do modal principal (#1021). Não há escolha de "manter a
 * assinatura cobrando": o que o servidor faz com um `sub_…` ainda no doc
 * (cortesia anterior à E3, ou assinatura do Asaas herdada) é cancelá-lo no
 * gateway DONO dele e tirá-lo do doc — Stripe grava antes e tolera falha no
 * cancelamento; Asaas cancela antes e, se falhar, devolve `aborted`
 * (`gateway_cancel_failed`) com o doc intacto (#795, BIL-2). Este diálogo
 * ainda NÃO traduz esse `details.code` como o `ApplyLifetimeFreeDialog` faz
 * — o operador vê o texto cru do servidor (ver #796).
 *
 * Permissão no servidor: `requireTenantOperator` com
 * `staffRoles: ADMIN_PERMISSIONS.manageCourtesy` (ROOT/FINANCE —
 * `docs/permissions.md`, #795 ADM-7) ou ADMIN do CRM (`crmAdminOnly`).
 *
 * Usado por: `BillingActions` (só com `commercial_mode` efetivo COURTESY),
 * montado em `routes/TenantDetailPage.tsx` (rota `/tenants/:id`, menu
 * "Tenants"), admin web. Spec: `docs/screens/tenant-detail-admin.md`.
 *
 * Dados: `RemoveLifetimeFreeInput` (`packages/types`); efeito em
 * `tenants/{tid}/billing/subscription` e audit `LIFETIME_FREE_REMOVED`
 * (`apps/functions/src/admin/removeLifetimeFree.ts`). `currentCategory` vem de
 * `courtesy.category` ou do legado `discount.lifetime_free_category`.
 *
 * Falhas conhecidas:
 *  - Até 2026-10-04 (#796) esta docstring descrevia o comportamento anterior à
 *    #738 ("se for paga, mantém e só tira o cupom"), que não existe mais.
 *
 * Histórico:
 *  - 2026-06-10 03:23 +0200 — saiu a escolha "manter assinatura cobrando".
 *  - 2026-09-25 11:11 +0200 — (#738) cortesia como tipo comercial; o servidor
 *    deixou de manter assinatura.
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function RemoveLifetimeFreeDialog({
  open,
  tenantId,
  tenantName,
  currentCategory,
  onClose,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setConfirmOpen(false);
    setError(null);
  }, [open]);

  /** Limpa o erro e devolve o controle ao pai. */
  function close(): void {
    setError(null);
    onClose();
  }

  /** Exige motivo antes de abrir o `AdminConfirm`. */
  function requestConfirm(): void {
    if (reason.trim().length === 0) {
      setError(t('billing.lifetimeFree.remove.errors.reasonRequired'));
      return;
    }
    setError(null);
    setConfirmOpen(true);
  }

  /** Chama `removeLifetimeFree`; erro cru do servidor vai para o `Alert` do modal (#1021). */
  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await removeLifetimeFreeCallable({
        tenantId,
        reason: reason.trim(),
      });
      toast.success(t('billing.lifetimeFree.remove.success'));
      setConfirmOpen(false);
      close();
    } catch (err) {
      setConfirmOpen(false);
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        visible={open && !confirmOpen}
        // #1021 (2ª auditoria 2026-10-25 15:52 +0100): com a ação em voo, Esc/X/clique fora
        // fechavam o modal e a falha caía no `Alert` dele, já fechado — ninguém a via.
        onDismiss={(): void => { if (!(submitting)) { close(); } }}
        header={t('billing.lifetimeFree.remove.title', { tenantName })}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={close} disabled={submitting}>
                {t('tenantDetail.actions.cancel')}
              </Button>
              <Button variant="primary" onClick={requestConfirm} disabled={submitting}>
                {t('billing.lifetimeFree.remove.removeButton')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {currentCategory !== null && (
            <Box variant="p" color="text-body-secondary">
              {t('billing.lifetimeFree.remove.currentCategory', {
                category: t(`billing.lifetimeFree.category.${currentCategory}`, {
                  defaultValue: currentCategory,
                }),
              })}
            </Box>
          )}

          <FormField label={t('billing.lifetimeFree.remove.reasonLabel')}>
            <Input
              value={reason}
              onChange={({ detail }): void => {
                setReason(detail.value);
              }}
              placeholder={t('billing.lifetimeFree.remove.reasonPlaceholder')}
            />
          </FormField>
          {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
              do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
          {error !== null && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      </Modal>

      <AdminConfirm
        visible={confirmOpen}
        title={t('billing.lifetimeFree.remove.confirmTitle')}
        confirmLabel={t('billing.lifetimeFree.remove.confirmApply')}
        cancelLabel={t('tenantDetail.actions.cancel')}
        loading={submitting}
        onConfirm={(): void => {
          void submit();
        }}
        onDismiss={(): void => {
          setConfirmOpen(false);
        }}
      >
        <Box variant="p">
          {t('billing.lifetimeFree.remove.confirmDescription', { tenantName })}
        </Box>
      </AdminConfirm>
    </>
  );
}
